import { ReactElement, useEffect, useRef, useState } from "react";
import { Box, styled } from "@mui/material";

import { DynamicTable } from "..";
import { AnalyticsTableFooter } from "@/components/table/analytics-table-chrome";
import { tokens } from "@/tokens";

export const StyledBox = styled(Box)(() => ({
  flex: 1,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  "& .MuiTable-root": {
    borderCollapse: "collapse",
    minWidth: "initial",
    tableLayout: "fixed",
    width: "100%",

    "& .MuiTableCell-root": {
      border: 0,
      fontSize: { xs: "9px", md: "10px", lg: "11px" },
    },

    "& .MuiTableHead-root .MuiTableCell-root": {
      padding: { xs: "3px 4px !important", md: "5px 6px !important" },
      fontSize: tokens.fontSize.xs,
      fontWeight: tokens.fontWeight.semibold,
      whiteSpace: "nowrap",
      lineHeight: "16px",
      height: "auto",
      minHeight: "32px",
      backgroundColor: `${tokens.bg.muted} !important`,
      color: `${tokens.text.primary} !important`,
      textTransform: "none !important" as any,
      letterSpacing: "normal !important" as any,
    },

    "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
      borderRadius: `${tokens.radius.md} 0 0 ${tokens.radius.md}`,
    },
    "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
      borderRadius: `0 ${tokens.radius.md} ${tokens.radius.md} 0`,
    },

    "& .MuiTableBody-root .MuiTableRow-root": {
      backgroundColor: `${tokens.bg.surface} !important`,
      "&:hover": { backgroundColor: `${tokens.bg.hover} !important` },
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: { xs: "3px 4px !important", md: "5px 6px !important" },
      borderBlock: `1px solid ${tokens.border.default}`,
      fontSize: { xs: "9px", md: "10px", lg: "11px" },
      whiteSpace: "normal",
      wordBreak: "break-word",
      lineHeight: 1.2,
    },

    // First column specific styling in body to prevent wrapping
    "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
      whiteSpace: "nowrap",
      wordBreak: "keep-all",
    },

    "& .MuiTableFooter-root": {
      position: "sticky",
      bottom: 0,
      zIndex: 2,
    },
    "& .MuiTableFooter-root .MuiTableCell-root": {
      background: tokens.bg.muted,
      fontSize: tokens.fontSize.xs,
      fontWeight: tokens.fontWeight.semibold,
      color: tokens.text.primary,
      padding: { xs: "6px 4px !important", md: "8px 6px !important" },
      lineHeight: "16px",
      height: "auto",
    },
    "& .MuiTableFooter-root .MuiTableCell-root:first-of-type": {
      borderRadius: `${tokens.radius.md} 0 0 ${tokens.radius.md}`,
    },
    "& .MuiTableFooter-root .MuiTableCell-root:last-of-type": {
      borderRadius: `0 ${tokens.radius.md} ${tokens.radius.md} 0`,
    },
  },
  // Custom scrollbar styling for better visibility
  "&::-webkit-scrollbar": {
    width: 4,
    height: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: tokens.scrollbar.track,
  },
  "&::-webkit-scrollbar-thumb": {
    background: tokens.scrollbar.thumb,
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: tokens.scrollbar.thumbHover,
  },
}));

interface IVendorsTable<T> extends Omit<DynamicTableProps<T>, "data"> {
  footerColumns: VendorSection[];
  footerData: Record<string, number>;
  data: T[];
  fillerRowCount?: number; // number of empty rows to insert before footer
}

const VendorsTable = <T extends IBase>({
  footerColumns,
  footerData,
  data,
  fillerRowCount = 0,
  ...tableProps
}: IVendorsTable<T>): ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  const recomputeScrollability = () => {
    const el = containerRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight > el.clientHeight + 1; // allow rounding
    setIsScrollable(scrollable);
  };

  useEffect(() => {
    // recompute when data length or requested filler changes
    requestAnimationFrame(recomputeScrollability);
  }, [data?.length, fillerRowCount]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => recomputeScrollability());
    observer.observe(el);
    window.addEventListener("resize", recomputeScrollability);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recomputeScrollability);
    };
  }, []);

  // Cap fillers so total visible rows (data + fillers) never exceeds 5
  const maxAllowedFillers = Math.max(0, 10 - (Array.isArray(data) ? data.length : 0));
  const effectiveFillerRowCount = isScrollable
    ? 0
    : Math.min(Math.max(0, fillerRowCount), maxAllowedFillers);

  return (
    <StyledBox ref={containerRef}>
      <DynamicTable
        {...tableProps}
        data={data}
        stickyHeader
        hidePagination
        hideScrollBars={false}
        isFullHeight
        square
        borderLess
        contentIdKey={tableProps.contentIdKey}
        containerStyles={{
          overflow: "auto",
          maxWidth: "100%",
        }}
        fillerRowCount={effectiveFillerRowCount}
        renderFooter={() => (
          <AnalyticsTableFooter
            sections={footerColumns}
            data={footerData}
            columns={tableProps.columns}
          />
        )}
      />
    </StyledBox>
  );
};

export default VendorsTable;

